import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as turf from "@turf/turf";
import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { interpolateTurbo } from "d3-scale-chromatic";
import CartesianPlane3D from "./RTFOWellbore3DCartesianPlane3D";
import WellMesh from "./RTFOWellbore3DMesh";
import WellboreUI from "./RTFOWellbore3DUI";
import * as THREE from "three";
import {
  getLICoordinates,
  getXSphericalCoordinates,
  getYSphericalCoordinates,
} from "./geometryFunctions";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import SeismicityMesh from "./RTFOWellbore3DSeismicityMesh";
import { degToRad } from "three/src/math/MathUtils";
import { useSelector } from "react-redux";
import { selectRTFO } from "../../reducers/rtfoSlice";
import { convertMetricValue, getMetricParamName } from "./RTFOMetricsPanel";
import { getMetricValue } from "./RTFOMapWithWellbore";
import uuid from "react-uuid";
import RTFO3DCompass from "./RTFO3DCompass";

const wellColors = [
  0xf44336, 0xe91e63, 0x9c27b0, 0x2196f3, 0x4caf50, 0xff9800, 0xffeb3b,
];

function wellSurveyToPoints(wellsSurvey, size, verticalScaleMultiplier) {
  let tempWellPoints = [];
  const maxAxis = new THREE.Vector3(0, 0, 0);
  let minAxis = null;
  const newScale = new THREE.Vector2(0, 0);

  for (const well of Object.values(wellsSurvey)) {
    let totalX = 0;
    let totalY = 0;
    let totalZ = 0;

    const { surveyData } = well;

    // eslint-disable-next-line no-loop-func
    tempWellPoints.push(
      surveyData.map((value, index) => {
        if (index === 0 || index === 1) {
          return new THREE.Vector3(0, 0, 0);
        };

        const diffMeasureDepth = Math.abs(
          surveyData[index].measuredDepth - surveyData[index - 1].measuredDepth
        );

        const x = getXSphericalCoordinates(
          diffMeasureDepth,
          value.direction,
          value.deviation
        );

        const z = getYSphericalCoordinates(
          diffMeasureDepth,
          value.direction,
          value.deviation
        );

        totalX += x;
        totalY = value.trueVerticalDepth;
        totalZ += z;

        const coordinates = new THREE.Vector3(totalX, totalY, totalZ).multiply(
          new THREE.Vector3(1, -1, 1)
        );

        return coordinates;
      })
    );
  };

  for (const well of tempWellPoints) {
    for (const point of well) {
      const newPoint = point?.clone()?.multiply(
        new THREE.Vector3(1, -1, 1)
      );

      if (newScale.x < Math.abs(point.x)) {
        newScale.setX(Math.abs(newPoint.x));
      };
      if (newScale.x < Math.abs(point.z)) {
        newScale.setX(Math.abs(newPoint.z));
      };
      if (newScale.y < Math.abs(point.y)) {
        newScale.setY(Math.abs(newPoint.y));
      };

      if (minAxis === null) {
        minAxis = newPoint.clone();
      };
      if (maxAxis?.x < newPoint?.x) {
        maxAxis.setX(newPoint?.x);
      };
      if (minAxis?.x > newPoint?.x) {
        minAxis.setX(newPoint?.x);
      };
      if (maxAxis?.y < newPoint?.y) {
        maxAxis.setY(newPoint?.y);
      };
      if (minAxis?.y > newPoint?.y) {
        minAxis.setY(newPoint?.y);
      };
      if (maxAxis?.z < newPoint?.z) {
        maxAxis.setZ(newPoint?.z);
      };
      if (minAxis?.z > newPoint?.z) {
        minAxis.setZ(newPoint?.z);
      };
    };
  };

  const newScale3D = new THREE.Vector3(newScale?.x, newScale?.y, newScale?.x);

  const tempAxisScale = newScale3D
    ?.clone()
    ?.divide(new THREE.Vector3(size / 2, size / (4 * verticalScaleMultiplier || 1), size / 2));

  return {
    tempWellPoints,
    maxAxis,
    minAxis,
    tempAxisScale,
  };
};

function offsetWellboreByCoordinates(
  originCoord,
  wellboreCoord,
  points,
  scale
) {
  const position = coordinatesToCartesian3DPosition(originCoord, wellboreCoord);

  return points?.map((value) => {
    return new THREE.Vector3().addVectors(value, position);
  });
};

export function coordinatesToCartesian3DPosition(zeroCoord, coord, scale) {
  try {
    const distance = turf.distance(zeroCoord, coord, { units: "meters" });

    let angle = turf.bearing(zeroCoord, coord);

    if (angle < 0) {
      angle = 360 + angle;
    };

    if (scale === undefined) {
      const xPos = distance * Math.cos(THREE.MathUtils.degToRad(angle));
      const zPos = distance * Math.sin(THREE.MathUtils.degToRad(angle));
      return new THREE.Vector3(xPos, 0, zPos);
    } else {
      const xPos =
        (distance / scale?.x) * Math.cos(THREE.MathUtils.degToRad(angle));
      const zPos =
        (distance / scale?.z) * Math.sin(THREE.MathUtils.degToRad(angle));
      return new THREE.Vector3(xPos, 0, zPos);
    };
  } catch (err) {
    console.log(err);
    return undefined;
  };
};

export function checkStageEventIntersectionBack(
  point,
  angle,
  curve,
  curveSurvey
) {
  if (
    !point instanceof THREE.Vector3 ||
    !curve instanceof THREE.CatmullRomCurve3
  ) {
    throw new Error(
      "Function args should be vector3, radAngle, THREE CURVE, Curve Survey"
    );
  };

  const point2D = new THREE.Vector2(point.x, point.z);

  const indexHorizontal = findIndexWellboreHorizontalFase(curveSurvey);

  const p2 = curve?.points[indexHorizontal];
  const p1 = curve?.points[indexHorizontal + 10];

  const p1_2D = new THREE.Vector2(p1?.x, p1?.z);
  const p2_2D = new THREE.Vector2(p2?.x, p2?.z);

  const pdir = new THREE.Vector2().subVectors(p2_2D, p1_2D)?.normalize();
  const pointDir = new THREE.Vector2(
    Math.cos(angle),
    Math.sin(angle)
  )?.normalize();
  const result = intersectionSegment(
    point2D?.clone(),
    pointDir?.clone(),
    p2_2D?.clone(),
    new THREE.Vector2().addVectors(p2_2D, pdir.clone().multiply(500))
  );

  if (result !== null) {
    return new THREE.Vector3(result?.x, p2?.y, result?.y);
  } else return null;
};

export function intersectionLineCurve(point, angle, curve, curveSurvey) {
  if (
    !point instanceof THREE.Vector3 ||
    !curve instanceof THREE.CatmullRomCurve3
  ) {
    throw new Error(
      "Function args should be vector3, radAngle, THREE CURVE, Curve Survey"
    );
  };

  const point2D = new THREE.Vector2(point.x, point.z);

  const dir = new THREE.Vector2(Math.cos(angle), Math.sin(angle));
  const indexHorizontal = findIndexWellboreHorizontalFase(curveSurvey);
  for (let i = indexHorizontal; i < curve?.points?.length - 1; i++) {
    const p1 = curve?.points[i];
    const p2 = curve?.points[i + 1];

    const p1_2D = new THREE.Vector2(p1.x, p1.z);
    const p2_2D = new THREE.Vector2(p2.x, p2.z);

    const intersecao = intersectionSegment(point2D, dir, p1_2D, p2_2D);
    if (intersecao !== null) {
      return new THREE.Vector3().addVectors(p1, p2).divideScalar(2);
    };
  };
  return null;
};

export function findIndexWellboreHorizontalFase(wellboreSurveyData) {
  for (let i = 0; i < wellboreSurveyData?.length - 5; i++) {
    if (
      wellboreSurveyData?.[i]?.deviation >= 80 &&
      wellboreSurveyData?.[i + 1].deviation >= 80 &&
      wellboreSurveyData?.[i + 2].deviation >= 80 &&
      wellboreSurveyData?.[i + 3].deviation >= 80 &&
      wellboreSurveyData?.[i + 4].deviation >= 80
    ) {
      return i;
    };
  };
  return null;
};

export function findIndexWellboreHorizontalFase2(wellboreSurveyData) {
  for (let i = 0; i < wellboreSurveyData?.length - 5; i++) {
    if (
      wellboreSurveyData?.[i]?.deviation >= 75 &&
      wellboreSurveyData?.[i + 1].deviation >= 75 &&
      wellboreSurveyData?.[i + 2].deviation >= 75 &&
      wellboreSurveyData?.[i + 3].deviation >= 75 &&
      wellboreSurveyData?.[i + 4].deviation >= 75
    ) {
      return i;
    };
  };
  return null;
};

function intersectionSegment(point, direction, p1, p2) {
  const det = direction.x * (p2.y - p1.y) - direction.y * (p2.x - p1.x);

  const t =
    ((p1.x - point.x) * (p1.y - p2.y) - (p1.y - point.y) * (p1.x - p2.x)) / det;
  const u =
    ((p1.x - point.x) * direction.y - (p1.y - point.y) * direction.x) / det;

  if (u >= 0 && u <= 1) {
    const intersecaoX = point.x + t * direction.x;
    const intersecaoY = point.y + t * direction.y;
    return new THREE.Vector2(intersecaoX, intersecaoY);
  };

  return null;
};

export default function RTFOWellbore3D({
  stressShadow,
  metric,
  setMetric,
  allMetrics,
  darkMode,
  isFDI,
  width,
  height,
  allInterferenceLineCoordinates,
  isMetricsMap,
  dailyStages
}) {
  const size = 150;
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const surveyData = currentRTFO?.surveys;
  const perfScheduleData = currentRTFO?.perfSchedule;
  const monitorSurveys = currentRTFO?.monitorSurveys;
  const monitorWellEvents = currentRTFO?.monitorEvents;

  const units = currentRTFO?.settings?.units || "metric";
  const metricMinMax = getMetricMinMax(metric, units);

  //how much meters is in one three js size unit
  const [wellsPoints, setWellsPoints] = useState(null);
  const [monitorWellsPoints, setMonitorWellsPoints] = useState(null);
  const [wellsStages, setWellsStages] = useState(null);
  const [seismicityPoints, setSeismicityPoints] = useState(null);
  const [wellsMetadata, setWellsMetadata] = useState({
    axisScale: undefined,
    minAxisPoint: undefined,
    maxAxisPoint: undefined,
  });
  const [cameraInitialPosition, setCameraInitialPosition] = useState();
  const [cameraInitialTarget, setCameraInitialTarget] = useState();
  const [heightMultiplier, setHeightMultiplier] = useState(1);
  const [widthMultiplier, setWidthMultiplier] = useState(0.5);
  const [verticalScaleMultiplier, setVerticalScaleMultiplier] = useState(1 / 5);
  const [seismicityFilter, setSeismicityFilter] = useState([]);
  const [hideFracGeometry, setHideFracGeometry] = useState(false);
  const [gridOffset, setGridOffset] = useState(new THREE.Vector3(0, 0, 0));

  const darkTheme = createTheme({
    palette: {
      mode: "dark",
    },
  });
  const lightTheme = createTheme({
    palette: {
      mode: "light",
    },
  });

  const threeRef = useRef();
  const orbitRef = useRef();

  const stageMonitorEvents = useMemo(() => {
    try {
      return monitorWellEvents?.filter(
        (e) => e?.name !== "Stage Start" && e?.monitorUWI !== undefined
      );
    }
    catch (err) {
      console.log(err);
      return [];
    };
  }, [monitorWellEvents]);

  const seismicityData = useMemo(() => {
    if (!currentRTFO?.seismicData?.realtime?.length > 0) return [];

    const filteredEvents = [];

    for (let i = 0; i < currentRTFO?.seismicData?.realtime?.length; i++) {
      const event = currentRTFO?.seismicData?.realtime?.[i];

      let isEventFilteredOut = false;
      if (seismicityFilter?.length > 0) {
        if (seismicityFilter?.includes(0.75) && event?.localMagnitude < 1) {
          isEventFilteredOut = true;
        } else if (
          seismicityFilter?.includes(1.5) &&
          event?.localMagnitude >= 1 &&
          event?.localMagnitude < 2
        ) {
          isEventFilteredOut = true;
        } else if (
          seismicityFilter?.includes(2.5) &&
          event?.localMagnitude >= 2 &&
          event?.localMagnitude < 3
        ) {
          isEventFilteredOut = true;
        } else if (
          seismicityFilter?.includes(3.5) &&
          event?.localMagnitude >= 3 &&
          event?.localMagnitude < 4
        ) {
          isEventFilteredOut = true;
        } else if (
          seismicityFilter?.includes(4.5) &&
          event?.localMagnitude >= 4
        ) {
          isEventFilteredOut = true;
        };
      };

      if (!isEventFilteredOut) filteredEvents.push(event);
    };

    return filteredEvents;
  }, [currentRTFO?.seismicData?.realtime, seismicityFilter]);

  useEffect(() => {
    if (
      surveyData &&
      surveyData !== undefined &&
      perfScheduleData &&
      perfScheduleData !== undefined
    ) {
      const {
        tempWellPoints,
        maxAxis,
        minAxis,
        tempAxisScale
      } = wellSurveyToPoints(surveyData.wells, size, verticalScaleMultiplier);
      setWellsPoints(() => {
        return tempWellPoints.map((wells) => {
          return wells.map((value) => {
            return value.clone().divide(tempAxisScale);
          });
        });
      });
      setWellsMetadata((prev) => {
        return {
          ...prev,
          //the max and min points are inverted, thats why i and inverting here the values
          minAxisPoint: maxAxis.clone().divide(tempAxisScale).multiply(new THREE.Vector3(1, -1, 1)),
          maxAxisPoint: minAxis.clone().divide(tempAxisScale).multiply(new THREE.Vector3(1, -1, 1)),
          axisScale: tempAxisScale,
        };
      });

      setGridOffset((_) => {
        if (verticalScaleMultiplier === 1) {
          return new THREE.Vector3(0, 0, 0);
        }
        const tempOffsets = [];
        for (const [index, wellPoints] of tempWellPoints?.entries()) {
          const firstPositionIndex = findIndexWellboreHorizontalFase2(Object.values(surveyData?.wells?.[Object.keys(surveyData?.wells)?.[index]]?.surveyData));
          tempOffsets.push(wellPoints?.[firstPositionIndex]?.y);
        }
        let bigNumber = 0;
        for (const height of tempOffsets) {
          if (bigNumber < Math.abs(height)) {
            bigNumber = height;
          }
        }

        return new THREE.Vector3(0, bigNumber / tempAxisScale?.y, 0);
      });
    };
  }, [surveyData, perfScheduleData, size, verticalScaleMultiplier]);

  useEffect(() => {
    if (
      isFDI === true &&
      monitorSurveys !== undefined &&
      Object.values(monitorSurveys).length > 0 &&
      wellsMetadata?.axisScale !== undefined
    ) {
      const { tempWellPoints } = wellSurveyToPoints(monitorSurveys, size);
      const firstWellData = Object.values(surveyData?.wells)[0];
      const firstWellCoodinates = [
        firstWellData?.surfaceLon,
        firstWellData?.surfaceLat,
      ];
      const offsetPoints = [];
      for (const [wellIndex, well] of Object.values(monitorSurveys).entries()) {
        offsetPoints.push(
          offsetWellboreByCoordinates(
            firstWellCoodinates,
            [well?.surfaceLon, well?.surfaceLat],
            tempWellPoints[wellIndex],
            wellsMetadata?.axisScale
          )
        );
      };
      setMonitorWellsPoints(() => {
        return offsetPoints.map((wells) => {
          return wells.map((value) => {
            return value.clone().divide(wellsMetadata.axisScale);
          });
        });
      });
    };
  }, [
    isFDI,
    monitorSurveys,
    size,
    surveyData?.wells,
    wellsMetadata?.axisScale,
  ]);

  useEffect(() => {
    if (wellsMetadata?.axisScale !== undefined && wellsPoints) {
      const wellsSurveyData = Object.values(surveyData?.wells);

      //well loop
      const tempWellStages = [];
      for (const [wellIndex, well] of Object.values(
        perfScheduleData?.wells
      ).entries()) {
        //stages loop
        const newStages = [];
        for (const stage of Object.values(well.stages)) {
          const clusters = [];
          //cluster loop
          for (const cluster of Object.values(stage.clusters)) {
            const measuredDepth = cluster.shotDepth;
            for (
              let i = 0;
              i < wellsSurveyData[wellIndex].surveyData.length - 1;
              i++
            ) {
              if (
                wellsSurveyData[wellIndex].surveyData[i].measuredDepth <
                measuredDepth &&
                wellsSurveyData[wellIndex].surveyData[i + 1].measuredDepth >
                measuredDepth
              ) {
                const t = Math.abs(
                  (measuredDepth -
                    wellsSurveyData[wellIndex].surveyData[i].measuredDepth) /
                  (wellsSurveyData[wellIndex].surveyData[i].measuredDepth -
                    wellsSurveyData[wellIndex].surveyData[i + 1]
                      .measuredDepth)
                );
                const coordinates = getLICoordinates(
                  wellsPoints[wellIndex][i],
                  wellsPoints[wellIndex][i + 1],
                  t
                );
                clusters.push(coordinates);
              };
            };
          };
          newStages.push(clusters);
        };
        tempWellStages.push(newStages);
      };
      setWellsStages(tempWellStages);
    };
  }, [perfScheduleData, surveyData, wellsMetadata, wellsPoints]);

  useEffect(() => {
    if (
      seismicityData &&
      seismicityData !== undefined &&
      wellsMetadata &&
      wellsMetadata.axisScale !== undefined &&
      surveyData &&
      surveyData !== undefined
    ) {
      const firstWellData = Object.values(surveyData?.wells)[0];
      const firstWellCoodinates = [
        firstWellData.surfaceLon,
        firstWellData.surfaceLat,
      ];
      const points = [];

      for (const seismicity of seismicityData) {
        const distance = turf.distance(
          firstWellCoodinates,
          [seismicity.longitude, seismicity.latitude],
          { units: "meters" }
        );

        //filter distances greater than 20km
        if (distance < 20000) {
          let angle = turf.bearing(
            firstWellCoodinates,
            //inverse in the ssd convention
            [seismicity.longitude, seismicity.latitude]
          );

          if (angle < 0) {
            angle = 360 + angle;
          };

          //using threejs concept of x,y,z
          const xPos =
            (distance / wellsMetadata.axisScale.getComponent(0)) *
            Math.cos(degToRad(angle));
          const yPos =
            ((seismicity.depth * 1000) /
              wellsMetadata.axisScale.getComponent(1)) *
            -1;
          const zPos =
            (distance / wellsMetadata.axisScale.getComponent(2)) *
            Math.sin(degToRad(angle));

          points.push({
            position: new THREE.Vector3(xPos, yPos, zPos),
            magnitude: seismicity.localMagnitude,
          });
        };
      };
      setSeismicityPoints(points);
    };
  }, [seismicityData, wellsMetadata, surveyData]);

  const Seismicities = useCallback(() => {
    if (
      size &&
      size !== undefined &&
      seismicityPoints &&
      seismicityPoints !== undefined &&
      Array.isArray(seismicityPoints)
    ) {
      return seismicityPoints.map((point, index) => {
        return <SeismicityMesh key={index} {...point} />;
      });
    } else return null;
  }, [size, seismicityPoints]);

  const Wells = useCallback(() => {
    try {
      return Object.keys(surveyData?.wells)?.map((_, index) => {
        return (
          <WellMesh
            key={uuid()}
            name={Object.keys(surveyData?.wells)[index]}
            axisScale={wellsMetadata?.axisScale}
            points={wellsPoints?.[index]}
            stages={wellsStages?.[index]}
            color={wellColors?.[index]}
            widthMultiplier={widthMultiplier}
            heightMultiplier={heightMultiplier}
            hideFracGeometry={hideFracGeometry}
            currentRTFO={currentRTFO}
            curveSurvey={Object.values(surveyData?.wells?.[Object.keys(surveyData?.wells)?.[index]]?.surveyData)}
            metric={metric}
            setMetric={setMetric}
            stressShadow={stressShadow}
            metricMinMax={metricMinMax}
            stageMonitorEvents={stageMonitorEvents}
            isFDI={isFDI}
            monitorWellsPoints={monitorWellsPoints}
            allInterferenceLineCoordinates={allInterferenceLineCoordinates}
            isMetricsMap={isMetricsMap}
            dailyStages={dailyStages}
            onlyHorizontalFase={(verticalScaleMultiplier !== 1)}
            verticalScaleMultiplier={verticalScaleMultiplier}
            gridOffset={gridOffset}
            size={size}
          />
        );
      });
    }
    catch (err) {
      return undefined;
    };
  }, [surveyData?.wells, wellsMetadata?.axisScale, wellsPoints, wellsStages, widthMultiplier, heightMultiplier, hideFracGeometry, currentRTFO, metric, setMetric, stressShadow, metricMinMax, stageMonitorEvents, isFDI, monitorWellsPoints, allInterferenceLineCoordinates, isMetricsMap, dailyStages, verticalScaleMultiplier, gridOffset]);

  const MonitorWells = useCallback(() => {
    try {
      return Object?.keys(monitorSurveys)?.map((_, index) => {
        return (
          <WellMesh
            key={index}
            name={Object.keys(monitorSurveys)?.[index]}
            axisScale={wellsMetadata?.axisScale}
            points={monitorWellsPoints?.[index]}
            color={"lightgrey"}
            currentRTFO={currentRTFO}
            isFDI={isFDI}
            isMonitorWell={true}
          />
        );
      });
    }
    catch (err) {
      return undefined;
    };
  }, [
    currentRTFO,
    isFDI,
    monitorSurveys,
    monitorWellsPoints,
    wellsMetadata?.axisScale,
  ]);

  //Set camera initial position and target
  useEffect(() => {
    if (
      wellsMetadata.minAxisPoint !== undefined &&
      wellsMetadata.maxAxisPoint !== undefined
    ) {
      if (
        gridOffset === 0
      ) {
        const target = new THREE.Vector3()
          .addVectors(wellsMetadata.maxAxisPoint, wellsMetadata.minAxisPoint)
          // .multiply(new THREE.Vector3(1, 1 / (1 / verticalScaleMultiplier), 1))
          .divide(new THREE.Vector3(2, 1.2, 2));
        setCameraInitialTarget(target);

        const position = new THREE.Vector3()
          .addVectors(wellsMetadata.maxAxisPoint, wellsMetadata.minAxisPoint)
          // .multiply(new THREE.Vector3(1, 1 / (1 / verticalScaleMultiplier), 1))
          .multiply(new THREE.Vector3(1.5, 1, 1.5))
          .divide(new THREE.Vector3(1, 1.8, 1));
        setCameraInitialPosition(position);
      }
      else if (
        gridOffset?.y !== undefined &&
        gridOffset !== 0
      ) {
        const target = new THREE.Vector3()
          .addVectors(wellsMetadata.maxAxisPoint, wellsMetadata.minAxisPoint)
          .divide(new THREE.Vector3(2, 1.2, 2))
          .setY(gridOffset?.y);
        setCameraInitialTarget(target);

        const position = new THREE.Vector3()
          .addVectors(wellsMetadata.maxAxisPoint, wellsMetadata.minAxisPoint)
          .multiply(new THREE.Vector3(1.5, 1, 1.5))
          .divide(new THREE.Vector3(1, 1.8, 1))
          .setY(gridOffset?.y);
        setCameraInitialPosition(position);
      }
    }
  }, [gridOffset, verticalScaleMultiplier, wellsMetadata]);

  function resetCamera() {
    if (orbitRef.current !== undefined) {
      threeRef.current?.camera?.position?.fromArray(
        cameraInitialPosition.toArray()
      );
      orbitRef.current?.position?.fromArray(cameraInitialPosition.toArray());
      orbitRef.current?.target?.fromArray(cameraInitialTarget.toArray());
    }
  };

  return (
    <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
      <Canvas
        camera={{
          position: cameraInitialPosition,
        }}
        style={{
          height: height !== undefined ? height : "calc(100vh - 250px)",
          width: width !== undefined ? width : "100%",
          borderRadius: "5px",
        }}
      >
        <color attach="background" args={[darkMode ? "black" : "white"]} />
        <ThreeForwardRef ref={threeRef} />
        <OrbitControls
          ref={orbitRef}
          minDistance={8}
          maxDistance={320}
          target={cameraInitialTarget}
          position={cameraInitialPosition}
        />
        <ambientLight intensity={1} />
        <directionalLight
          position={[2, 3, -0.16]}
          intensity={5}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <CartesianPlane3D
          size={size}
          wellsMetaData={wellsMetadata}
          offset={gridOffset}
          divisions={10}
          cameraInitialPosition={cameraInitialPosition}
          units={units}
          darkMode={darkMode}
        />
        <Seismicities />
        <Wells />
        {isFDI === true ? <MonitorWells /> : null}
      </Canvas>
      <RTFO3DCompass wellbore3DRef={threeRef} mainOrbitControlRef={orbitRef} darkMode={darkMode} />
      <WellboreUI
        colorScale={interpolateTurbo}
        range={metricMinMax}
        setHeightMultiplier={setHeightMultiplier}
        setWidthMultiplier={setWidthMultiplier}
        widthMultiplier={widthMultiplier}
        heightMultiplier={heightMultiplier}
        setVerticalScaleMultiplier={setVerticalScaleMultiplier}
        verticalScaleMultiplier={verticalScaleMultiplier}
        hideFracGeometry={hideFracGeometry}
        setHideFracGeometry={setHideFracGeometry}
        metric={metric}
        units={units}
        setMetric={setMetric}
        allMetrics={allMetrics}
        seismicityFilter={seismicityFilter}
        setSeismicityFilter={setSeismicityFilter}
        resetCamera={resetCamera}
        isFDI={isFDI}
        darkMode={darkMode}
      />
    </ThemeProvider>
  );

  function getMetricMinMax(metric, units) {
    try {
      const values = [];
      const stages = {};
      const metricParamName = getMetricParamName(metric);
      // add stages from current pad
      for (let stageName of currentRTFO?.stages) {
        const value = +convertMetricValue(
          metricParamName,
          getMetricValue(
            stageName,
            metric,
            stressShadow,
            currentRTFO?.stageData,
            currentRTFO?.settings,
            currentRTFO?.perfSchedule,
            widthMultiplier,
            currentRTFO?.stages,
            units,
            currentRTFO?.monitorEvents,
            currentRTFO?.fiberData
          ),
          units
        );
        if (value > 0) {
          values.push(value);
          stages[stageName] = value;
        };
      };

      const minValue = Math.min(...values) * 0.95 || 0;
      const maxValue = Math.max(...values) * 1.05 || 1;

      if (metric === "Seismicity Risk") {
        return [0, 4];
      } else {
        return [minValue, maxValue];
      }
    } catch (error) {
      console.log(error);
      return 0;
    };
  };
};

const ThreeForwardRef = forwardRef((props, ref) => {
  const { scene, camera } = useThree();
  const { refCallback } = props;
  const tempRef = ref;
  useImperativeHandle(
    ref,
    () => ({
      scene,
      camera,
    }),
    [scene, camera]
  );

  if (refCallback !== undefined && tempRef?.current === undefined) {
    refCallback();
  };
});
